import apiService from "../api-service";
// Types for preview responses
interface StoryboardFrame {
  frame_id: string;
  doc_id: string;
  page: number;
  dimension: string | null;
}

interface StoryboardPreviewResponse {
  frames: StoryboardFrame[];
  next_page_start: number | null;
  total_pages?: number;
}

interface ManuscriptContent {
  type: string;
  level: string | null;
  text: string | null;
  data: any;
}

interface BannerOption {
  heading: string;
  details: string;
}

interface ManuscriptPreviewResponse {
  full_content: ManuscriptContent[];
  banner_options: {
    [key: string]: BannerOption;
  };
}

interface BriefSize {
  Sheet: string;
  Field_Name: string;
  Sizes: string[];
}

interface BriefPreviewResponse {
  sizes: BriefSize[];
}

interface FteStoryboardTypeResponse {
  fte_storyboard_type: "Figma" | "eWizard" | "unknown";
}

/**
 * Retrieve storyboard previews for non-animated banner channels
 * @param channel_id - Channel ID
 * @param docId - Document ID
 * @param accessToken - Optional access token for authentication
 * @returns Direct array of storyboard frames
 */
export async function getStoryboardPreviews(
  channel_id: string,
  docId: string,
  client_id: string,
  accessToken?: string,
): Promise<StoryboardFrame[]> {
  if (!docId || !docId.trim()) {
    throw new Error("Document ID is required");
  }

  if (!channel_id || !channel_id.trim()) {
    throw new Error("Channel ID is required");
  }

  try {
    const response: any = await apiService.get({
      url: `previews/storyboard`,
      baseUrlKey: "apiUrl",
      accessToken,
      axiosConfig: {
        params: {
          channel_id,
          doc_id: docId,
          client_id: client_id,
        },
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch storyboard previews:", error);
    throw new Error(
      error?.message ||
        "Failed to fetch storyboard previews. Please try again.",
    );
  }
}

/**
 * Retrieve paginated storyboard previews for animated banner channel
 * @param channel_id - Channel ID
 * @param docId - Document ID
 * @param accessToken - Optional access token for authentication
 * @param pageStart - Page start number for pagination
 * @param pageEnd - Page end number for pagination
 * @returns Paginated response with frames array and next_page_start
 */
export async function getAnimatedBannerStoryboardPreviews(
  channel_id: string,
  docId: string,
  client_id: string,
  accessToken?: string,
  pageStart?: number,
  pageEnd?: number,
): Promise<StoryboardPreviewResponse> {
  if (!docId || !docId.trim()) {
    throw new Error("Document ID is required");
  }

  if (!channel_id || !channel_id.trim()) {
    throw new Error("Channel ID is required");
  }

  try {
    const params: any = {
      channel_id,
      doc_id: docId,
      client_id: client_id,
    };

    // Add pagination params if provided
    if (pageStart !== undefined) {
      params.page_start = pageStart;
    }
    if (pageEnd !== undefined) {
      params.page_end = pageEnd;
    }

    const response: any = await apiService.get({
      url: `previews/storyboard`,
      baseUrlKey: "apiUrl",
      accessToken,
      axiosConfig: {
        params,
      },
    });

    return response.data;
  } catch (error: any) {
    console.error(
      "Failed to fetch animated banner storyboard previews:",
      error,
    );
    throw new Error(
      error?.message ||
        "Failed to fetch animated banner storyboard previews. Please try again.",
    );
  }
}

/**
 * Retrieve manuscript previews for a given document
 * @param docId - Document ID
 * @param accessToken - Optional access token for authentication
 * @param channel_id - Optional channel ID
 * @returns Manuscript preview data with full content and banner options
 */
export async function getManuscriptPreviews(
  docId: string,
  client_id: string,
  accessToken?: string,
  channel_id?: string,
): Promise<ManuscriptPreviewResponse> {
  if (!docId || !docId.trim()) {
    throw new Error("Document ID is required");
  }

  try {
    const params: { doc_id: string; channel_id?: string; client_id: string } = {
      doc_id: docId,
      client_id: client_id,
    };

    if (channel_id) {
      params.channel_id = channel_id;
    }

    const response: any = await apiService.get({
      url: `previews/manuscript`,
      baseUrlKey: "apiUrl",
      accessToken,
      axiosConfig: {
        params,
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch manuscript previews:", error);
    throw new Error(
      error?.message ||
        "Failed to fetch manuscript previews. Please try again.",
    );
  }
}

/**
 * Retrieve brief previews for a given document
 * @param docId - Document ID
 * @param client_id - Client ID
 * @param accessToken - Optional access token for authentication
 * @returns Brief preview data with sizes information
 */
export async function getBriefPreviews(
  docId: string,
  client_id: string,
  accessToken?: string,
): Promise<BriefPreviewResponse> {
  if (!docId || !docId.trim()) {
    throw new Error("Document ID is required");
  }

  try {
    const response: any = await apiService.get({
      url: `previews/brief`,
      baseUrlKey: "apiUrl",
      accessToken,
      axiosConfig: {
        params: {
          doc_id: docId,
          client_id: client_id,
        },
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch brief previews:", error);
    throw new Error(
      error?.message || "Failed to fetch brief previews. Please try again.",
    );
  }
}

/**
 * Retrieve FTE storyboard type for a given document
 * @param docId - Document ID
 * @param client_id - Client ID
 * @param accessToken - Optional access token for authentication
 * @returns FTE storyboard type ('figma', 'eWizard', or 'unknown')
 */
export async function getFteStoryboardType(
  docId: string,
  client_id: string,
  accessToken?: string,
): Promise<FteStoryboardTypeResponse> {
  if (!docId || !docId.trim()) {
    console.log("Invalid document ID:", docId);
    throw new Error("Document ID is required");
  }

  try {
    const response: any = await apiService.get({
      url: `classifications/fte-storyboard-type`,
      baseUrlKey: "apiUrl",
      accessToken,
      axiosConfig: {
        params: {
          doc_id: docId,
          client_id: client_id,
        },
      },
    });

    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch FTE storyboard type:", error);
    throw new Error(
      error?.message ||
        "Failed to fetch FTE storyboard type. Please try again.",
    );
  }
}
